
import { useEffect, useState, useContext } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  AuthContext,
} from "context";

import AuthService from "services/auth-service";
import { Can } from "Can";

// ... (imports aynı kalıyor)

function Sidenav({ color = "info", brand = "", brandName, routes, ...rest }) {
  const authContext = useContext(AuthContext);
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;

  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, []);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const handleLogOut = async () => {
    try {
      await AuthService.logout();
      authContext.logout();
    } catch (err) {
      console.error(err);
    }
  };

  const renderNestedCollapse = (collapse) =>
    collapse.map(({ name, route, key, href }) =>
      href ? (
        <Link key={key} href={href} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
          <SidenavItem name={name} color={color} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
          <SidenavItem name={name} color={color} active={route === pathname} nested />
        </NavLink>
      )
    );

    const renderCollapse = (collapses) =>
        collapses.map(({ name, collapse, route, href, key, type }) => {
            const openUpOnlyForTMS = key === "shipping-files";
            if (collapse) {
                return (
                    <SidenavList key={key}>
                        <SidenavItem
                            color={color}
                            name={name}
                            active={key === itemParentName ? "isParent" : false}
                            open={openNestedCollapse === key}
                            openUp={key === "shipping-files"}
                            onClick={({ currentTarget }) =>
                                openNestedCollapse === key &&
                                currentTarget.classList.contains("MuiListItem-root")
                                    ? setOpenNestedCollapse(false)
                                    : setOpenNestedCollapse(key)
                            }
                        >
                            {renderNestedCollapse(collapse)}
                        </SidenavItem>
                    </SidenavList>
                );
            }

      if (name === "Logout") {
        return (
          <SidenavList key={key}>
            <MDBox>
              <MDButton fullWidth variant="gradient" color={color} onClick={handleLogOut}>
                Log Out
              </MDButton>
            </MDBox>
          </SidenavList>
        );
      }

      const Item = (
        <SidenavItem
          color={color}
          name={name}
          active={pathname.includes(route)} // 🔥 Burası değişti
        />
      );

      const WrappedItem = href ? (
        <Link key={key} href={href} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
          {type ? <Can I="edit" this={type}>{Item}</Can> : Item}
        </Link>
      ) : (
        <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
          {type ? <Can I="edit" a={type}>{Item}</Can> : Item}
        </NavLink>
      );

      return <SidenavList key={key}>{WrappedItem}</SidenavList>;
    });

  const renderRoutes = routes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route }) => {
      if (type === "collapse") {
        if (href) {
          return (
            <Link key={key} href={href} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
              <SidenavCollapse name={name} icon={icon} color={color} active={key === collapseName} noCollapse={noCollapse} />
            </Link>
          );
        } else if (noCollapse && route) {
          return (
            <NavLink to={route} key={key}>
              <SidenavCollapse name={name} icon={icon} color={color} noCollapse={noCollapse} active={key === collapseName}>
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          return (
              <SidenavCollapse
                  key={key}
                  name={name}
                  icon={icon}
                  color={color}
                  active={key === collapseName}
                  open={openCollapse === key}
                  openUp={key === "shipping-files"}
                  onClick={() =>
                      openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key)
                  }
              >
                  {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
          );
        }
      }

      if (type === "title") {
        return (
          <MDTypography
            key={key}
            sx={(theme) => ({ color: theme.palette[color]?.main })}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      }

      if (type === "divider") {
        return (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return null;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
        <MDBox
            pt={3}
            pb={2}
            px={3}
            textAlign="center"
        >
            <MDBox
                component={NavLink}
                to="/"
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                    textDecoration: "none",
                    p: 1.2,
                    borderRadius: "16px",
                    background: darkMode
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(255,255,255,0.10)",
                    backdropFilter: "blur(12px)",
                }}
            >
                {brand && (
                    <MDBox
                        component="img"
                        src={brand}
                        alt="Brand"
                        width="2.4rem"
                        sx={{
                            borderRadius: "12px",
                            boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
                        }}
                    />
                )}

                <MDBox
                    width={!brandName && "100%"}
                    sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
                >
                    <MDTypography
                        component="h6"
                        variant="button"
                        fontWeight="bold"
                        color={whiteSidenav ? "dark" : "white"}
                    >
                        {brandName}
                    </MDTypography>

                    {!miniSidenav && (
                        <MDTypography
                            variant="caption"
                            color={whiteSidenav ? "text" : "white"}
                            sx={{ opacity: 0.7 }}
                        >
                            Management Panel
                        </MDTypography>
                    )}
                </MDBox>
            </MDBox>
        </MDBox>
        <Divider light={(!darkMode && !whiteSidenav && !transparentSidenav) || (darkMode && !transparentSidenav && whiteSidenav)} />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;

